// client/src/pages/JobDetailPage.jsx
import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Plus, X, Check, Trash2, ChevronDown, ChevronUp,
  CalendarClock, FileText, Loader2, Clock, User, CheckCircle, XCircle,
  Send, MessageSquare, Paperclip, ExternalLink, ClipboardCheck, Search,
} from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import {
  useJob, useUpdateJob,
  useAddJobTask, useUpdateJobTask, useDeleteJobTask,
  useAddJobPart, useUpdateJobPart, useDeleteJobPart,
  useParts, useGenerateQuoteFromJob,
  useAcceptJobRequest, useDeclineJobRequest,
  useJobMessages, useAddJobMessage,
  useAddPartReceipt, usePartReceipts,
  useMotChecklist, useUpdateMotChecklistItem,
  useToggleInvestigation,
} from '../api/hooks'
import CalendarPicker from '../components/ui/CalendarPicker'
import TimePicker from '../components/ui/TimePicker'

// ─── Constants ────────────────────────────────────────────────────────────────

const JOB_STATUS_OPTIONS = [
  { v: 'in_progress',       l: 'In Progress'      },
  { v: 'waiting_on_parts',  l: 'Waiting on Parts' },
  { v: 'awaiting_approval', l: 'Awaiting Approval' },
  { v: 'approved',          l: 'Approved'          },
  { v: 'completed',         l: 'Completed'         },
  { v: 'cancelled',         l: 'Cancelled'         },
]

const JOB_STATUS_STYLES = {
  requested:         'bg-blue-500/15 text-blue-400',
  quoted:            'bg-purple-500/15 text-purple-400',
  awaiting_approval: 'bg-yellow-500/15 text-yellow-400',
  approved:          'bg-indigo-500/15 text-indigo-400',
  in_progress:       'bg-amber-500/15 text-amber-400',
  waiting_on_parts:  'bg-orange-500/15 text-orange-400',
  completed:         'bg-green-500/15 text-green-400',
  cancelled:         'bg-gray-500/15 text-gray-400',
}

const JOB_STATUS_LABELS = {
  requested: 'Requested', quoted: 'Quoted', awaiting_approval: 'Awaiting Approval',
  approved: 'Approved', in_progress: 'In Progress', waiting_on_parts: 'Waiting on Parts',
  completed: 'Completed', cancelled: 'Cancelled',
}

const TASK_STATUS_STYLES = {
  pending:     'bg-gray-500/15 text-gray-400',
  in_progress: 'bg-amber-500/15 text-amber-400',
  completed:   'bg-green-500/15 text-green-400',
  cancelled:   'bg-red-500/15 text-red-400',
}

const PART_STATUS_STYLES = {
  required: 'bg-gray-500/15 text-gray-400',
  ordered:  'bg-blue-500/15 text-blue-400',
  received: 'bg-indigo-500/15 text-indigo-400',
  fitted:   'bg-green-500/15 text-green-400',
  returned: 'bg-red-500/15 text-red-400',
}

const PART_STATUS_OPTIONS = ['required', 'ordered', 'received', 'fitted', 'returned']

const GARAGE_ROLES = ['garage_user', 'garage_admin', 'platform_admin']

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

// ─── Plate ────────────────────────────────────────────────────────────────────

function Plate({ registration }) {
  return (
    <span className="inline-block font-mono font-bold tracking-widest text-sm
                     bg-yellow-400 text-black px-3 py-1 rounded">
      {registration}
    </span>
  )
}

// ─── Conversation thread ──────────────────────────────────────────────────────

function ConversationThread({ jobId }) {
  const { user }   = useAuth()
  const { data: messages = [], isLoading } = useJobMessages(jobId)
  const addMessage = useAddJobMessage()
  const [text,     setText]     = useState('')
  const bottomRef  = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  const isGarageRole = (role) => GARAGE_ROLES.includes(role)

  const handleSend = async (e) => {
    e.preventDefault()
    if (!text.trim()) return
    try {
      await addMessage.mutateAsync({ jobId, message: text.trim() })
      setText('')
    } catch {
      toast.error('Failed to send message')
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend(e)
    }
  }

  const fmtTime = (ts) => {
    const d = new Date(ts)
    const today = new Date()
    const isToday = d.toDateString() === today.toDateString()
    if (isToday) return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) +
      ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-[var(--border)]">
        <MessageSquare size={16} className="text-[var(--text-muted)]" />
        <h3 className="font-medium text-[var(--text-primary)]">
          Conversation
          {messages.length > 0 && (
            <span className="ml-1 text-[var(--text-muted)] font-normal">({messages.length})</span>
          )}
        </h3>
      </div>

      {/* Messages */}
      <div className="max-h-80 overflow-y-auto p-4 space-y-3">
        {isLoading ? (
          <div className="text-center py-4 text-sm text-[var(--text-muted)]">Loading…</div>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-[var(--text-muted)] py-4">
            No messages yet. Start the conversation below.
          </p>
        ) : (
          messages.map(m => {
            const isOwn     = m.user_id === user?.id
            const isGarage  = isGarageRole(m.sender_role)

            return (
              <div key={m.id}
                className={clsx('flex gap-2.5', isOwn ? 'flex-row-reverse' : 'flex-row')}>
                {/* Avatar */}
                <div className={clsx(
                  'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5',
                  isGarage
                    ? 'bg-indigo-600/20 text-indigo-400'
                    : 'bg-blue-600/20 text-blue-400'
                )}>
                  {m.sender_name?.[0]?.toUpperCase() ?? '?'}
                </div>

                {/* Bubble */}
                <div className={clsx('max-w-[75%]', isOwn ? 'items-end' : 'items-start', 'flex flex-col gap-1')}>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-medium text-[var(--text-secondary)]">
                      {isOwn ? 'You' : m.sender_name || 'Unknown'}
                    </span>
                    <span className={clsx(
                      'text-[10px] px-1.5 py-0.5 rounded font-medium',
                      isGarage
                        ? 'bg-indigo-500/15 text-indigo-400'
                        : 'bg-blue-500/15 text-blue-400'
                    )}>
                      {isGarage ? 'Garage' : 'Customer'}
                    </span>
                    <span className="text-[10px] text-[var(--text-muted)]">{fmtTime(m.created_at)}</span>
                  </div>
                  <div className={clsx(
                    'px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words',
                    isOwn
                      ? 'bg-indigo-600 text-white rounded-tr-sm'
                      : 'bg-[var(--bg-overlay)] text-[var(--text-primary)] rounded-tl-sm'
                  )}>
                    {m.message}
                  </div>
                </div>
              </div>
            )
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="flex gap-2 px-4 py-3 border-t border-[var(--border)]">
        <textarea
          rows={1}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message… (Enter to send)"
          className={clsx(inputCls, 'resize-none flex-1')}
          style={{ minHeight: '38px', maxHeight: '120px' }}
        />
        <button
          type="submit"
          disabled={!text.trim() || addMessage.isPending}
          className="shrink-0 p-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40
                     text-white rounded-lg transition-colors"
        >
          {addMessage.isPending
            ? <Loader2 size={15} className="animate-spin" />
            : <Send size={15} />}
        </button>
      </form>
    </div>
  )
}

// ─── Task row ─────────────────────────────────────────────────────────────────

function TaskRow({ task, jobId, editable }) {
  const updateTask = useUpdateJobTask()
  const deleteTask = useDeleteJobTask()

  const cycleStatus = () => {
    if (!editable) return
    const order = ['pending', 'in_progress', 'completed']
    const next  = order[(order.indexOf(task.status) + 1) % order.length]
    updateTask.mutate({ jobId, taskId: task.id, status: next })
  }

  const handleDelete = async () => {
    if (!window.confirm('Remove this task?')) return
    try {
      await deleteTask.mutateAsync({ jobId, taskId: task.id })
      toast.success('Task removed')
    } catch {
      toast.error('Remove failed')
    }
  }

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl">
      <button
        onClick={cycleStatus}
        disabled={!editable || updateTask.isPending}
        className={clsx(
          'w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors',
          !editable ? 'cursor-default' : '',
          task.status === 'completed'
            ? 'bg-green-500 border-green-500'
            : task.status === 'in_progress'
              ? 'border-amber-400'
              : 'border-[var(--border-strong)]'
        )}
      >
        {task.status === 'completed' && <Check size={11} className="text-white" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={clsx('text-sm font-medium',
          task.status === 'completed' ? 'line-through text-[var(--text-muted)]' : 'text-[var(--text-primary)]')}>
          {task.title}
        </p>
        {task.description && (
          <p className="text-xs text-[var(--text-muted)] truncate">{task.description}</p>
        )}
      </div>
      <span className={clsx('px-2 py-0.5 rounded text-xs font-medium shrink-0',
        TASK_STATUS_STYLES[task.status] || 'bg-gray-500/15 text-gray-400')}>
        {task.status.replace('_', ' ')}
      </span>
      {task.labour_hours > 0 && (
        <span className="text-xs text-[var(--text-muted)] shrink-0">{task.labour_hours}h</span>
      )}
      {editable && (
        <button onClick={handleDelete} className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors shrink-0">
          <Trash2 size={13} />
        </button>
      )}
    </div>
  )
}

// ─── Add Task form ─────────────────────────────────────────────────────────────

function AddTaskForm({ jobId, onDone }) {
  const addTask = useAddJobTask()
  const [title, setTitle] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!title.trim()) return
    try {
      await addTask.mutateAsync({ jobId, title: title.trim() })
      setTitle('')
      onDone?.()
    } catch {
      toast.error('Failed to add task')
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <input
        type="text"
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="Task description…"
        className={clsx(inputCls, 'flex-1')}
        autoFocus
      />
      <button type="submit" disabled={addTask.isPending || !title.trim()}
        className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                   text-white text-sm rounded-lg transition-colors shrink-0">
        Add
      </button>
      <button type="button" onClick={() => onDone?.()}
        className="px-3 py-2 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                   text-[var(--text-muted)] text-sm rounded-lg transition-colors shrink-0">
        <X size={14} />
      </button>
    </form>
  )
}

// ─── Add Part modal ───────────────────────────────────────────────────────────

function AddPartModal({ jobId, vehicleId, onClose }) {
  const addPart  = useAddJobPart()
  const { data: catalogue = [] } = useParts({ vehicleId })
  const [form, setForm] = useState({
    partId: '', name: '', partNumber: '', supplier: '',
    quantity: '1', estimatedCost: '0',
  })
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleCatalogueSelect = (e) => {
    const part = catalogue.find(p => p.id === e.target.value)
    if (part) {
      setForm(prev => ({
        ...prev,
        partId:        part.id,
        name:          part.name,
        partNumber:    part.part_number || '',
        supplier:      part.supplier    || '',
        estimatedCost: String(part.unit_cost || 0),
      }))
    } else {
      setForm(prev => ({ ...prev, partId: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await addPart.mutateAsync({
        jobId,
        partId:        form.partId        || null,
        name:          form.name,
        partNumber:    form.partNumber     || null,
        supplier:      form.supplier       || null,
        quantity:      parseInt(form.quantity) || 1,
        estimatedCost: parseFloat(form.estimatedCost) || 0,
      })
      toast.success('Part added')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add part')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-sm bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">Add Part</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-3">
          {catalogue.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">From catalogue</label>
              <select value={form.partId} onChange={handleCatalogueSelect} className={inputCls}>
                <option value="">— or enter manually —</option>
                {catalogue.map(p => (
                  <option key={p.id} value={p.id}>{p.name}{p.part_number ? ` (${p.part_number})` : ''}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Name</label>
            <input type="text" value={form.name} onChange={set('name')}
              placeholder="e.g. Oil filter" className={inputCls} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Part no.</label>
              <input type="text" value={form.partNumber} onChange={set('partNumber')}
                placeholder="OEM-12345" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Supplier</label>
              <input type="text" value={form.supplier} onChange={set('supplier')}
                placeholder="GSF, Euro Car Parts…" className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Qty</label>
              <input type="number" min="1" value={form.quantity} onChange={set('quantity')} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Est. cost (£)</label>
              <input type="number" min="0" step="0.01" value={form.estimatedCost} onChange={set('estimatedCost')} className={inputCls} />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={addPart.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {addPart.isPending ? 'Adding…' : 'Add Part'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Part row ─────────────────────────────────────────────────────────────────

function PartRow({ part, jobId, editable, isCustomer }) {
  const updatePart  = useUpdateJobPart()
  const deletePart  = useDeleteJobPart()
  const addReceipt  = useAddPartReceipt()
  const { data: receipts = [] } = usePartReceipts(!isCustomer ? part.id : null)

  const [expanded,    setExpanded]    = useState(false)
  const [editingOf,   setEditingOf]   = useState(false)
  const [orderedFrom, setOrderedFrom] = useState(part.ordered_from || '')
  const fileRef = useRef(null)

  const handleStatusChange = (status) => {
    updatePart.mutate({ jobId, partId: part.id, status })
  }

  const handleDelete = async () => {
    if (!window.confirm('Remove this part?')) return
    try {
      await deletePart.mutateAsync({ jobId, partId: part.id })
      toast.success('Part removed')
    } catch {
      toast.error('Remove failed')
    }
  }

  const handleSaveOrderedFrom = async () => {
    try {
      await updatePart.mutateAsync({ jobId, partId: part.id, orderedFrom })
      setEditingOf(false)
      toast.success('Saved')
    } catch {
      toast.error('Save failed')
    }
  }

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      await addReceipt.mutateAsync({ jobPartId: part.id, jobId, file })
      toast.success('Receipt uploaded')
    } catch {
      toast.error('Upload failed')
    }
    e.target.value = ''
  }

  const hasExpand = !isCustomer

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3">
        {hasExpand && (
          <button
            onClick={() => setExpanded(o => !o)}
            className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors shrink-0"
          >
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--text-primary)]">{part.name}</p>
          <p className="text-xs text-[var(--text-muted)]">
            {part.part_number && `${part.part_number} · `}
            Qty {part.quantity}
            {!isCustomer && part.estimated_cost > 0 && ` · £${Number(part.estimated_cost).toFixed(2)} est.`}
            {!isCustomer && part.actual_cost != null && ` / £${Number(part.actual_cost).toFixed(2)} actual`}
            {!isCustomer && part.ordered_from && ` · ${part.ordered_from}`}
          </p>
        </div>
        {receipts.length > 0 && !isCustomer && (
          <span className="flex items-center gap-1 text-xs text-[var(--text-muted)] shrink-0">
            <Paperclip size={11} />
            {receipts.length}
          </span>
        )}
        {editable ? (
          <select
            value={part.status}
            onChange={e => handleStatusChange(e.target.value)}
            disabled={updatePart.isPending}
            className={clsx(
              'px-2 py-1 rounded text-xs font-medium border-0 outline-none cursor-pointer shrink-0',
              PART_STATUS_STYLES[part.status] || 'bg-gray-500/15 text-gray-400'
            )}
          >
            {PART_STATUS_OPTIONS.map(s => (
              <option key={s} value={s} className="bg-[var(--bg-surface)] text-[var(--text-primary)]">
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        ) : (
          <span className={clsx('px-2 py-0.5 rounded text-xs font-medium shrink-0',
            PART_STATUS_STYLES[part.status] || 'bg-gray-500/15 text-gray-400')}>
            {part.status.charAt(0).toUpperCase() + part.status.slice(1)}
          </span>
        )}
        {editable && (
          <button onClick={handleDelete} className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors shrink-0">
            <Trash2 size={13} />
          </button>
        )}
      </div>

      {expanded && !isCustomer && (
        <div className="px-4 pb-4 pt-2 border-t border-[var(--border)] space-y-3 bg-[var(--bg-base)]">
          {/* Ordered from */}
          <div>
            <p className="text-xs font-medium text-[var(--text-secondary)] mb-1">Ordered from</p>
            {editingOf ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={orderedFrom}
                  onChange={e => setOrderedFrom(e.target.value)}
                  placeholder="e.g. GSF Car Parts, Euro Car Parts…"
                  className={inputCls}
                  autoFocus
                />
                <button
                  onClick={handleSaveOrderedFrom}
                  disabled={updatePart.isPending}
                  className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                             text-white text-xs rounded-lg transition-colors shrink-0"
                >
                  Save
                </button>
                <button
                  onClick={() => { setEditingOf(false); setOrderedFrom(part.ordered_from || '') }}
                  className="px-3 py-2 bg-[var(--bg-overlay)] text-[var(--text-muted)]
                             text-xs rounded-lg transition-colors shrink-0"
                >
                  <X size={13} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-[var(--text-secondary)]">
                  {part.ordered_from || <span className="text-[var(--text-muted)] italic">Not set</span>}
                </span>
                {editable && (
                  <button
                    onClick={() => { setOrderedFrom(part.ordered_from || ''); setEditingOf(true) }}
                    className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                  >
                    Edit
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Receipts */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-[var(--text-secondary)]">
                Receipts {receipts.length > 0 && `(${receipts.length})`}
              </p>
              {editable && (
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={addReceipt.isPending}
                  className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300
                             disabled:opacity-50 transition-colors"
                >
                  <Paperclip size={11} />
                  {addReceipt.isPending ? 'Uploading…' : 'Upload'}
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={handleFileChange} />
            {receipts.length === 0 ? (
              <p className="text-xs text-[var(--text-muted)]">No receipts attached.</p>
            ) : (
              <div className="space-y-1">
                {receipts.map(r => (
                  <a
                    key={r.id}
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                  >
                    <Paperclip size={11} className="shrink-0" />
                    <span className="truncate">{r.filename}</span>
                    <ExternalLink size={10} className="shrink-0" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function JobDetailPage() {
  const { id }      = useParams()
  const navigate    = useNavigate()
  const { user }    = useAuth()
  const { data: job, isLoading, error } = useJob(id)
  const updateJob          = useUpdateJob()
  const generateQuote      = useGenerateQuoteFromJob()
  const acceptRequest      = useAcceptJobRequest()
  const declineRequest     = useDeclineJobRequest()
  const { data: motData }  = useMotChecklist(job?.is_mot_inspection ? id : null)
  const updateMotItem      = useUpdateMotChecklistItem()
  const toggleInvestigation = useToggleInvestigation()

  const [showAddTask,     setShowAddTask]     = useState(false)
  const [showAddPart,     setShowAddPart]     = useState(false)
  const [editingNotes,    setEditingNotes]    = useState(false)
  const [notesValue,      setNotesValue]      = useState('')
  const [tasksOpen,       setTasksOpen]       = useState(true)
  const [partsOpen,       setPartsOpen]       = useState(true)
  const [editingSchedule, setEditingSchedule] = useState(false)
  const [schedDate,       setSchedDate]       = useState('')
  const [schedTime,       setSchedTime]       = useState('')

  const isGarage   = GARAGE_ROLES.includes(user?.role)
  const isCustomer = user?.role === 'customer'
  const backPath   = isGarage ? '/garage/jobs' : '/repairs'

  const handleStatusChange = async (status) => {
    try {
      await updateJob.mutateAsync({ id, status })
      toast.success('Status updated')
    } catch {
      toast.error('Update failed')
    }
  }

  const handleSaveNotes = async () => {
    try {
      await updateJob.mutateAsync({ id, internalNotes: notesValue })
      toast.success('Notes saved')
      setEditingNotes(false)
    } catch {
      toast.error('Save failed')
    }
  }

  const handleEditSchedule = () => {
    setSchedDate(job.scheduled_date?.slice(0, 10) || '')
    setSchedTime(job.scheduled_time?.slice(0, 5) || '')
    setEditingSchedule(true)
  }

  const handleSaveSchedule = async () => {
    try {
      await updateJob.mutateAsync({
        id,
        scheduledDate: schedDate || null,
        scheduledTime: schedTime || null,
      })
      toast.success('Schedule saved')
      setEditingSchedule(false)
    } catch {
      toast.error('Save failed')
    }
  }

  const handleGenerateQuote = async () => {
    try {
      const quote = await generateQuote.mutateAsync(id)
      toast.success(`Quote ${quote.quote_number} created`)
      navigate('/garage/quotes')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate quote')
    }
  }

  const handleAccept = async () => {
    try {
      await acceptRequest.mutateAsync(id)
      toast.success('Job request accepted — now in progress')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to accept')
    }
  }

  const handleDecline = async () => {
    if (!window.confirm('Decline this job request? The customer will need to re-submit.')) return
    try {
      await declineRequest.mutateAsync(id)
      toast.success('Job request declined')
      navigate('/garage/jobs')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to decline')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse max-w-2xl">
        <div className="h-8 w-32 bg-[var(--bg-surface)] rounded-lg" />
        <div className="h-40 bg-[var(--bg-surface)] rounded-xl" />
        <div className="h-48 bg-[var(--bg-surface)] rounded-xl" />
      </div>
    )
  }

  if (error || !job) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate(backPath)}
          className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
          <ArrowLeft size={16} /> {isGarage ? 'Jobs' : 'Repairs'}
        </button>
        <p className="text-[var(--text-secondary)]">Job not found.</p>
      </div>
    )
  }

  const statusStyle = JOB_STATUS_STYLES[job.status] || 'bg-gray-500/15 text-gray-400'
  const isRequested = job.status === 'requested'
  const canQuote    = isGarage && !['completed', 'cancelled', 'requested'].includes(job.status)
  const canSchedule = isGarage && !['completed', 'cancelled', 'requested'].includes(job.status)

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  })

  const fmtScheduled = () => {
    if (!job.scheduled_date) return null
    const d = new Date(job.scheduled_date).toLocaleDateString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    })
    return job.scheduled_time ? `${d} at ${job.scheduled_time.slice(0, 5)}` : d
  }

  return (
    <div className="space-y-6 pb-12 max-w-2xl">
      {/* Back */}
      <button
        onClick={() => navigate(backPath)}
        className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
      >
        <ArrowLeft size={16} />
        {isGarage ? 'Jobs' : 'Repairs'}
      </button>

      {/* Header */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <h1 className="font-display text-2xl font-bold text-[var(--text-primary)]">{job.title}</h1>
          <div className="flex items-center gap-2 shrink-0 mt-1">
            {job.needs_investigation && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium
                               bg-purple-500/15 text-purple-400 border border-purple-500/30">
                <Search size={11} />
                Investigate
              </span>
            )}
            <span className={clsx('px-2.5 py-1 rounded text-xs font-medium', statusStyle)}>
              {JOB_STATUS_LABELS[job.status] || job.status}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap mb-3">
          {job.registration && <Plate registration={job.registration} />}
          {job.make && (
            <span className="text-sm text-[var(--text-secondary)]">{job.make} {job.model}</span>
          )}
        </div>

        {/* Requested by (garage view) */}
        {isGarage && isRequested && job.requested_by_name && (
          <div className="flex items-center gap-2 mb-3 text-sm text-[var(--text-secondary)]">
            <User size={13} className="text-blue-400 shrink-0" />
            <span>Requested by <strong className="text-[var(--text-primary)]">{job.requested_by_name}</strong></span>
            {job.requested_by_email && (
              <span className="text-[var(--text-muted)]">· {job.requested_by_email}</span>
            )}
          </div>
        )}

        {/* Preferred date */}
        {job.preferred_date && (
          <div className="flex items-center gap-2 mb-3 text-sm text-[var(--text-secondary)]">
            <CalendarClock size={13} className="text-indigo-400 shrink-0" />
            <span>
              {isCustomer ? 'Your preferred date:' : 'Customer preferred date:'}{' '}
              <strong className="text-[var(--text-primary)]">
                {fmtDate(job.preferred_date)}
              </strong>
            </span>
          </div>
        )}

        <div className="flex flex-wrap gap-4 text-xs text-[var(--text-muted)]">
          {job.date_booked && <span>Booked: {fmtDate(job.date_booked)}</span>}
          {job.date_started && <span>Started: {fmtDate(job.date_started)}</span>}
          {job.date_completed && <span>Completed: {fmtDate(job.date_completed)}</span>}
          {isGarage && job.labour_hours > 0 && (
            <span>{job.labour_hours}h labour @ £{Number(job.labour_rate || 0).toFixed(0)}/h</span>
          )}
          {job.mileage_at_job && <span>{Number(job.mileage_at_job).toLocaleString()} miles</span>}
        </div>

        {job.description && (
          <p className="text-sm text-[var(--text-secondary)] mt-4 leading-relaxed">{job.description}</p>
        )}

        {/* Garage actions — investigate flag + generate quote */}
        {canQuote && (
          <div className="mt-4 pt-4 border-t border-[var(--border)] space-y-3">
            {/* Needs Further Investigation toggle */}
            <div>
              <button
                onClick={() => toggleInvestigation.mutate(id)}
                disabled={toggleInvestigation.isPending}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50',
                  job.needs_investigation
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)] text-[var(--text-primary)] border border-[var(--border)]'
                )}
              >
                {toggleInvestigation.isPending
                  ? <><Loader2 size={14} className="animate-spin" /> Updating…</>
                  : job.needs_investigation
                    ? <><Check size={14} /> Needs Investigation</>
                    : <><Search size={14} /> Mark: Needs Investigation</>}
              </button>
              {job.needs_investigation && (
                <p className="text-xs text-purple-400 mt-1.5">
                  Flagged — further investigation required before quoting.
                </p>
              )}
            </div>
            {/* Generate Quote */}
            <div>
              <button
                onClick={handleGenerateQuote}
                disabled={generateQuote.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700
                           disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {generateQuote.isPending
                  ? <><Loader2 size={14} className="animate-spin" /> Generating…</>
                  : <><FileText size={14} /> Generate Quote</>}
              </button>
              <p className="text-xs text-[var(--text-muted)] mt-1.5">
                Creates a draft quote from labour hours and parts on this job.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Further investigation banner — customer view */}
      {isCustomer && job.needs_investigation && (
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <Search size={18} className="text-purple-400 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-purple-300 mb-1">Further Investigation Required</h3>
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                Your garage needs to carry out additional diagnostic work before they can provide a quote.
                They will be in touch once they have a clear picture of the repair required.
                No action is needed from you at this stage.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Accept / Decline — garage only, requested jobs */}
      {isGarage && isRequested && (
        <div className="bg-[var(--bg-surface)] border border-blue-500/30 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            <h3 className="font-medium text-[var(--text-primary)]">Customer Job Request</h3>
          </div>
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            Accept to start work, or decline to send it back to the customer.
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleAccept}
              disabled={acceptRequest.isPending || declineRequest.isPending}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700
                         disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {acceptRequest.isPending
                ? <><Loader2 size={14} className="animate-spin" /> Accepting…</>
                : <><CheckCircle size={14} /> Accept</>}
            </button>
            <button
              onClick={handleDecline}
              disabled={acceptRequest.isPending || declineRequest.isPending}
              className="flex items-center gap-2 px-4 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         disabled:opacity-50 text-[var(--text-secondary)] hover:text-red-400
                         text-sm font-medium rounded-lg transition-colors border border-[var(--border)]"
            >
              {declineRequest.isPending
                ? <><Loader2 size={14} className="animate-spin" /> Declining…</>
                : <><XCircle size={14} /> Decline</>}
            </button>
          </div>
        </div>
      )}

      {/* Conversation thread — all roles */}
      <ConversationThread jobId={id} />

      {/* Schedule — garage only, non-requested */}
      {canSchedule && (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CalendarClock size={16} className="text-[var(--text-muted)]" />
              <h3 className="font-medium text-[var(--text-primary)]">Scheduled Date</h3>
            </div>
            {!editingSchedule ? (
              <button
                onClick={handleEditSchedule}
                className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                {job.scheduled_date ? 'Edit' : 'Set date'}
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={handleSaveSchedule} disabled={updateJob.isPending}
                  className="text-xs text-indigo-400 hover:text-indigo-300 disabled:opacity-50">
                  Save
                </button>
                <button onClick={() => setEditingSchedule(false)} className="text-xs text-[var(--text-muted)]">
                  Cancel
                </button>
              </div>
            )}
          </div>

          {editingSchedule ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[var(--text-muted)] mb-1.5">Date</label>
                <CalendarPicker
                  value={schedDate}
                  onChange={setSchedDate}
                  placeholder="Pick a date…"
                />
              </div>
              <div>
                <label className="block text-xs text-[var(--text-muted)] mb-1.5">Time (optional)</label>
                <TimePicker
                  value={schedTime}
                  onChange={setSchedTime}
                  placeholder="Pick a time…"
                />
              </div>
            </div>
          ) : fmtScheduled() ? (
            <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
              <Clock size={13} className="shrink-0 text-indigo-400" />
              {fmtScheduled()}
            </div>
          ) : (
            <p className="text-sm text-[var(--text-muted)]">No date scheduled yet.</p>
          )}
        </div>
      )}

      {/* Status management — garage only, non-requested */}
      {isGarage && !isRequested && (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
          <h3 className="font-medium text-[var(--text-primary)] mb-3">Update Status</h3>
          <div className="flex flex-wrap gap-2">
            {JOB_STATUS_OPTIONS.map(({ v, l }) => (
              <button
                key={v}
                onClick={() => handleStatusChange(v)}
                disabled={job.status === v || updateJob.isPending}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50',
                  job.status === v
                    ? 'bg-indigo-600 text-white cursor-default'
                    : 'bg-[var(--bg-overlay)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]'
                )}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tasks */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <button
            onClick={() => setTasksOpen(o => !o)}
            className="flex items-center gap-2 font-medium text-[var(--text-primary)]"
          >
            {tasksOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            Tasks {job.tasks?.length > 0 && <span className="text-[var(--text-muted)] font-normal">({job.tasks.length})</span>}
          </button>
          {isGarage && !isRequested && !showAddTask && (
            <button
              onClick={() => setShowAddTask(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                         bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-lg transition-colors"
            >
              <Plus size={13} />
              Add Task
            </button>
          )}
        </div>

        {tasksOpen && (
          <div className="p-4 space-y-2">
            {showAddTask && (
              <AddTaskForm jobId={id} onDone={() => setShowAddTask(false)} />
            )}
            {job.tasks?.length === 0 && !showAddTask ? (
              <p className="text-sm text-[var(--text-muted)] py-2">No tasks yet.</p>
            ) : (
              job.tasks?.map(t => (
                <TaskRow key={t.id} task={t} jobId={id} editable={isGarage && !isRequested} />
              ))
            )}
          </div>
        )}
      </div>

      {/* Parts */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <button
            onClick={() => setPartsOpen(o => !o)}
            className="flex items-center gap-2 font-medium text-[var(--text-primary)]"
          >
            {partsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            Parts {job.parts?.length > 0 && <span className="text-[var(--text-muted)] font-normal">({job.parts.length})</span>}
          </button>
          {isGarage && !isRequested && (
            <button
              onClick={() => setShowAddPart(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                         bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-lg transition-colors"
            >
              <Plus size={13} />
              Add Part
            </button>
          )}
        </div>

        {partsOpen && (
          <div className="p-4 space-y-2">
            {job.parts?.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)] py-2">No parts listed.</p>
            ) : (
              job.parts?.map(p => (
                <PartRow key={p.id} part={p} jobId={id}
                  editable={isGarage && !isRequested}
                  isCustomer={isCustomer} />
              ))
            )}
            {job.parts?.length > 0 && (
              <div className="pt-2 border-t border-[var(--border)] text-xs text-[var(--text-muted)] flex justify-end gap-4">
                <span>
                  Est. total: £{job.parts.reduce((s, p) => s + Number(p.estimated_cost || 0) * (p.quantity || 1), 0).toFixed(2)}
                </span>
                {job.parts.some(p => p.actual_cost != null) && (
                  <span>
                    Actual total: £{job.parts.reduce((s, p) => s + Number(p.actual_cost ?? p.estimated_cost ?? 0) * (p.quantity || 1), 0).toFixed(2)}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Customer notes */}
      {job.customer_notes && (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
          <h3 className="font-medium text-[var(--text-primary)] mb-2">Customer Notes</h3>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">
            {job.customer_notes}
          </p>
        </div>
      )}

      {/* MOT Checklist — garage only, MOT inspection jobs */}
      {isGarage && job?.is_mot_inspection && motData && (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-[var(--border)]">
            <ClipboardCheck size={15} className="text-indigo-400" />
            <h3 className="font-medium text-[var(--text-primary)]">MOT Inspection Checklist</h3>
            {(() => {
              const all   = Object.values(motData.categories).flat()
              const done  = all.filter(i => i.result).length
              const total = all.length
              const fails = all.filter(i => i.result === 'fail').length
              return (
                <span className="ml-auto text-xs text-[var(--text-muted)]">
                  {done}/{total} checked
                  {fails > 0 && <span className="ml-2 text-red-400 font-medium">{fails} fail{fails > 1 ? 's' : ''}</span>}
                </span>
              )
            })()}
          </div>

          <div className="divide-y divide-[var(--border)]">
            {Object.entries(motData.categories).map(([category, items]) => (
              <details key={category} open className="group">
                <summary className="flex items-center justify-between px-5 py-3 cursor-pointer
                                    hover:bg-[var(--bg-overlay)] transition-colors list-none">
                  <span className="text-sm font-medium text-[var(--text-primary)]">{category}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[var(--text-muted)]">
                      {items.filter(i => i.result).length}/{items.length}
                    </span>
                    <ChevronDown size={14} className="text-[var(--text-muted)] group-open:rotate-180 transition-transform" />
                  </div>
                </summary>
                <div className="px-5 pb-3 space-y-1.5">
                  {items.map(item => {
                    const resultStyles = {
                      pass:           'bg-green-500/15 text-green-400 border-green-500/30',
                      fail:           'bg-red-500/15 text-red-400 border-red-500/30',
                      advisory:       'bg-amber-500/15 text-amber-400 border-amber-500/30',
                      not_applicable: 'bg-[var(--bg-overlay)] text-[var(--text-muted)] border-[var(--border)]',
                    }
                    return (
                      <div key={item.item_id}
                        className="flex items-center gap-3 py-2 border-b border-[var(--border)] last:border-0">
                        <span className="flex-1 text-sm text-[var(--text-secondary)]">{item.item}</span>
                        <div className="flex gap-1 shrink-0">
                          {[
                            { v: 'pass',           l: 'Pass',  cls: 'hover:bg-green-500/20 hover:text-green-400' },
                            { v: 'fail',           l: 'Fail',  cls: 'hover:bg-red-500/20 hover:text-red-400' },
                            { v: 'advisory',       l: 'Adv',   cls: 'hover:bg-amber-500/20 hover:text-amber-400' },
                            { v: 'not_applicable', l: 'N/A',   cls: 'hover:bg-[var(--bg-elevated)] hover:text-[var(--text-secondary)]' },
                          ].map(({ v, l, cls }) => (
                            <button
                              key={v}
                              onClick={() => updateMotItem.mutateAsync({ jobId: id, itemId: item.item_id, result: v })}
                              disabled={updateMotItem.isPending}
                              className={clsx(
                                'px-2 py-0.5 rounded text-xs font-medium border transition-colors disabled:opacity-40',
                                item.result === v
                                  ? resultStyles[v]
                                  : 'border-[var(--border)] text-[var(--text-muted)] ' + cls
                              )}
                            >
                              {l}
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </details>
            ))}
          </div>
        </div>
      )}

      {/* Internal notes — garage only */}
      {isGarage && (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-[var(--text-primary)]">Internal Notes</h3>
            {!editingNotes ? (
              <button
                onClick={() => { setNotesValue(job.internal_notes || ''); setEditingNotes(true) }}
                className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={handleSaveNotes} disabled={updateJob.isPending}
                  className="text-xs text-indigo-400 hover:text-indigo-300 disabled:opacity-50">
                  Save
                </button>
                <button onClick={() => setEditingNotes(false)} className="text-xs text-[var(--text-muted)]">
                  Cancel
                </button>
              </div>
            )}
          </div>
          {editingNotes ? (
            <textarea
              rows={4}
              value={notesValue}
              onChange={e => setNotesValue(e.target.value)}
              placeholder="Notes visible to garage staff only…"
              className={clsx(inputCls, 'resize-none')}
            />
          ) : job.internal_notes ? (
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">
              {job.internal_notes}
            </p>
          ) : (
            <p className="text-sm text-[var(--text-muted)]">No internal notes.</p>
          )}
        </div>
      )}

      {showAddPart && <AddPartModal jobId={id} vehicleId={job?.vehicle_id} onClose={() => setShowAddPart(false)} />}
    </div>
  )
}
